// components/CurrencyTrans.jsx
import React from 'react';

// Currency mapping with Arabic translations
const CURRENCY_MAP = {
  // Major Currencies
  USD: { ar: 'دولار أمريكي', symbol: '$', short: '$' },
  EUR: { ar: 'يورو', symbol: '€', short: '€' },
  GBP: { ar: 'جنيه إسترليني', symbol: '£', short: '£' },
  JPY: { ar: 'ين ياباني', symbol: '¥', short: '¥' },
  CNY: { ar: 'يوان صيني', symbol: '¥', short: '¥' },
  CHF: { ar: 'فرنك سويسري', symbol: 'CHF', short: 'CHF' },
  CAD: { ar: 'دولار كندي', symbol: 'C$', short: 'C$' },
  AUD: { ar: 'دولار أسترالي', symbol: 'A$', short: 'A$' },
  NZD: { ar: 'دولار نيوزيلندي', symbol: 'NZ$', short: 'NZ$' },
  SYR: { ar: 'ليرة سورية', symbol: 'ل.س', short: 'ل.س' },

  // Middle East Currencies
  SAR: { ar: 'ريال سعودي', symbol: 'ر.س', short: 'ر.س' },
  AED: { ar: 'درهم إماراتي', symbol: 'د.إ', short: 'د.إ' },
  QAR: { ar: 'ريال قطري', symbol: 'ر.ق', short: 'ر.ق' },
  KWD: { ar: 'دينار كويتي', symbol: 'د.ك', short: 'د.ك' },
  BHD: { ar: 'دينار بحريني', symbol: 'د.ب', short: 'د.ب' },
  OMR: { ar: 'ريال عماني', symbol: 'ر.ع', short: 'ر.ع' },
  JOD: { ar: 'دينار أردني', symbol: 'د.ا', short: 'د.ا' },
  LBP: { ar: 'ليرة لبنانية', symbol: 'ل.ل', short: 'ل.ل' },
  EGP: { ar: 'جنيه مصري', symbol: 'ج.م', short: 'ج.م' },
  IQD: { ar: 'دينار عراقي', symbol: 'د.ع', short: 'د.ع' },
  SYP: { ar: 'ليرة سورية', symbol: 'ل.س', short: 'ل.س' },
  YER: { ar: 'ريال يمني', symbol: 'ر.ي', short: 'ر.ي' },
  MAD: { ar: 'درهم مغربي', symbol: 'د.م', short: 'د.م' },
  DZD: { ar: 'دينار جزائري', symbol: 'د.ج', short: 'د.ج' },
  TND: { ar: 'دينار تونسي', symbol: 'د.ت', short: 'د.ت' },
  LYD: { ar: 'دينار ليبي', symbol: 'د.ل', short: 'د.ل' },
  SDG: { ar: 'جنيه سوداني', symbol: 'ج.س', short: 'ج.س' },
  
  // Asian Currencies
  INR: { ar: 'روبية هندية', symbol: '₹', short: '₹' },
  PKR: { ar: 'روبية باكستانية', symbol: 'Rs', short: 'Rs' },
  BDT: { ar: 'تاكا بنغلاديشي', symbol: '৳', short: '৳' },
  IDR: { ar: 'روبية إندونيسية', symbol: 'Rp', short: 'Rp' },
  MYR: { ar: 'رينغيت ماليزي', symbol: 'ر.م', short: 'ر.م' },
  SGD: { ar: 'دولار سنغافوري', symbol: 'S$', short: 'S$' },
  THB: { ar: 'بات تايلاندي', symbol: '฿', short: '฿' },
  VND: { ar: 'دونغ فيتنامي', symbol: '₫', short: '₫' },
  PHP: { ar: 'بيزو فلبيني', symbol: '₱', short: '₱' },
  KRW: { ar: 'وون كوري', symbol: '₩', short: '₩' },
  
  // Other Currencies
  RUB: { ar: 'روبل روسي', symbol: '₽', short: '₽' },
  BRL: { ar: 'ريال برازيلي', symbol: 'R$', short: 'R$' },
  ZAR: { ar: 'راند جنوب أفريقي', symbol: 'R', short: 'R' },
  TRY: { ar: 'ليرة تركية', symbol: '₺', short: '₺' },
  
  // Default fallback
  DEFAULT: { ar: 'عملة', symbol: '', short: '' }
};

/**
 * Currency translation function
 * @param {string} currencyCode - The currency code (e.g., 'USD', 'MYR', 'EGP')
 * @param {string} lang - Language code ('ar' for Arabic, any other for English)
 * @param {string} format - Format type: 'full' (full name), 'symbol' (currency symbol), 'short' (short code), 'auto' (default)
 * @returns {string} - Translated currency
 */
const currencyTrans = (currencyCode, lang = 'ar', format = 'auto') => {
  if (!currencyCode) return '';
  
  const currency = CURRENCY_MAP[currencyCode.toUpperCase()] || CURRENCY_MAP.DEFAULT;
  
  switch (format) {
    case 'full':
      return lang === 'ar' ? currency.ar : currencyCode.toUpperCase();
    case 'symbol':
      return currency.symbol || currencyCode.toUpperCase();
    case 'short':
      return lang === 'ar' ? currency.short : currencyCode.toUpperCase();
    case 'auto':
    default:
      return lang === 'ar' ? currency.short : currencyCode.toUpperCase();
  }
};

/**
 * Format price with currency
 * @param {number} amount - The price amount
 * @param {string} currencyCode - The currency code
 * @param {string} lang - Language code
 * @returns {string} - Formatted price with currency
 */
export const formatPrice = (amount, currencyCode, lang = 'ar') => {
  if (amount === undefined || amount === null) return '';
  
  const currencySymbol = currencyTrans(currencyCode, lang, 'auto');
  const formattedAmount = new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
  
  // For Arabic, currency symbol comes after the number
  return `${formattedAmount} ${currencySymbol}`;
};

/**
 * Get currency symbol only
 * @param {string} currencyCode - The currency code
 * @param {string} lang - Language code
 * @returns {string} - Currency symbol
 */
export const getCurrencySymbol = (currencyCode, lang = 'ar') => {
  return currencyTrans(currencyCode, lang, 'symbol');
};

/**
 * Get currency short name
 * @param {string} currencyCode - The currency code
 * @param {string} lang - Language code
 * @returns {string} - Currency short name
 */
export const getCurrencyShort = (currencyCode, lang = 'ar') => {
  return currencyTrans(currencyCode, lang, 'short');
};

/**
 * Get currency full name
 * @param {string} currencyCode - The currency code
 * @param {string} lang - Language code
 * @returns {string} - Currency full name
 */
export const getCurrencyFullName = (currencyCode, lang = 'ar') => {
  return currencyTrans(currencyCode, lang, 'full');
};

export default currencyTrans;